import { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import SimpleNavigation from './components/SimpleNavigation';
import { Footer } from './components/layout';
import {
  ToastContainer,
  OfflineIndicator,
} from './components/ui';
import { useToastStore } from './hooks/useToast';
// import { useNetworkStatus } from './hooks/useNetworkStatus';
import ErrorBoundary from './components/ErrorBoundary';
import { initSentry } from './utils/sentry';
import type { TestResult } from './types/test';
import type { DetailedAnalysis } from './utils/resultAnalyzer';
// import { useNetworkStatus } from './hooks/useNetworkStatus';

// Lazy load components for code splitting
const LandingPage = lazy(() => import('./components/LandingPage'));
const SampleQuestions = lazy(() => import('./components/SampleQuestions'));
const TestEngine = lazy(() => import('./components/test/TestEngine'));
const TestResultsPage = lazy(() => import('./components/TestResultsPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const IQGuidePage = lazy(() => import('./pages/IQGuidePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const ErrorHandlingDemo = lazy(() => import('./components/ErrorHandlingDemo'));
const ErrorHandlingShowcase = lazy(
  () => import('./components/ErrorHandlingShowcase')
);
const MobileLayoutTest = lazy(() => import('./components/MobileLayoutTest'));
const AdminPortal = lazy(() =>
  import('./components/admin/AdminPortal').then(module => ({
    default: module.AdminPortal,
  }))
);

type PageType =
  | 'landing'
  | 'questions'
  | 'test-standard'
  | 'test-comprehensive'
  | 'results';

function App() {
  const [currentPage, setCurrentPage] = useState<PageType>('landing');
  const [testResult, setTestResult] = useState<
    (TestResult & { fullAnalysis?: DetailedAnalysis }) | null
  >(null);
  const location = useLocation();
  const { toasts, removeToast } = useToastStore();
  // const networkStatus = useNetworkStatus(); // Network status handling available if needed

  useEffect(() => {
    // Initialize Sentry
    initSentry();
  }, []);

  const handlePageChange = (page: 'landing' | 'questions') => {
    setCurrentPage(page);
  };

  const handleStartRealTest = () => {
    setCurrentPage('landing');
    // Since this is now a free app, just go back to landing page
  };

  const handleStartTest = (testType: 'standard' | 'comprehensive') => {
    setCurrentPage(
      testType === 'standard' ? 'test-standard' : 'test-comprehensive'
    );
  };

  const handleExitTest = () => {
    setCurrentPage('landing');
    setTestResult(null);
  };

  const handleTestComplete = (
    result: TestResult & { fullAnalysis?: DetailedAnalysis }
  ) => {
    setTestResult(result);
    setCurrentPage('results');
  };

  const renderCurrentPage = () => {
    const LoadingSpinner = () => (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading...</p>
        </div>
      </div>
    );

    switch (currentPage) {
      case 'landing':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <LandingPage 
              onStartTest={handleStartTest} 
              onSampleTest={() => setCurrentPage('questions')}
            />
          </Suspense>
        );
      case 'questions':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <SampleQuestions onStartRealTest={handleStartRealTest} />
          </Suspense>
        );
      case 'test-standard':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <TestEngine
              testType="standard"
              onComplete={handleTestComplete}
              onExit={handleExitTest}
            />
          </Suspense>
        );
      case 'test-comprehensive':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <TestEngine
              testType="comprehensive"
              onComplete={handleTestComplete}
              onExit={handleExitTest}
            />
          </Suspense>
        );
      case 'results':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <TestResultsPage
              testResult={testResult}
              onExitTest={handleExitTest}
              onStartTest={handleStartTest}
            />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <LandingPage onStartTest={handleStartTest} />
          </Suspense>
        );
    }
  };



  const isPasswordResetPage = location.pathname === '/reset-password';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isPolicyPage = ['/privacy-policy', '/terms-of-service', '/cookie-policy', '/about', '/iq-guide', '/blog', '/help-center', '/contact', '/features'].includes(location.pathname);
  const showNavigation =
    !currentPage.startsWith('test-') && !isPasswordResetPage && !isAdminPage && !isPolicyPage;
  const showFooter =
    !currentPage.startsWith('test-') && !isPasswordResetPage && !isAdminPage && !isPolicyPage;

  return (
    <ErrorBoundary>
      <div className="relative min-h-screen flex flex-col">
        <OfflineIndicator />

        {showNavigation && (
          <SimpleNavigation
            currentPage={currentPage as 'landing' | 'questions'}
            onPageChange={handlePageChange}
          />
        )}

        <main className="flex-1">
          <Routes>
            <Route
              path="/reset-password"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading...</p>
                      </div>
                    </div>
                  }
                >
                  <PasswordResetPage />
                </Suspense>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading Privacy Policy...</p>
                      </div>
                    </div>
                  }
                >
                  <PrivacyPolicyPage />
                </Suspense>
              }
            />
            <Route
              path="/terms-of-service"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading Terms of Service...</p>
                      </div>
                    </div>
                  }
                >
                  <TermsOfServicePage />
                </Suspense>
              }
            />
            <Route
              path="/cookie-policy"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading Cookie Policy...</p>
                      </div>
                    </div>
                  }
                >
                  <CookiePolicyPage />
                </Suspense>
              }
            />
            <Route
              path="/about"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading About Page...</p>
                      </div>
                    </div>
                  }
                >
                  <AboutPage />
                </Suspense>
              }
            />
            <Route
              path="/iq-guide"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading IQ Guide...</p>
                      </div>
                    </div>
                  }
                >
                  <IQGuidePage />
                </Suspense>
              }
            />
            <Route
              path="/blog"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading Blog...</p>
                      </div>
                    </div>
                  }
                >
                  <BlogPage />
                </Suspense>
              }
            />
            <Route
              path="/help-center"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading Help Center...</p>
                      </div>
                    </div>
                  }
                >
                  <HelpCenterPage />
                </Suspense>
              }
            />
            <Route
              path="/contact"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                        <p className="text-lg font-medium">Loading Contact Page...</p>
                      </div>
                    </div>
                  }
                >
                  <ContactPage />
                </Suspense>
              }
            />
            <Route
              path="/features"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                      <div className="text-gray-600 text-center">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading Features...</p>
                      </div>
                    </div>
                  }
                >
                  <FeaturesPage />
                </Suspense>
              }
            />
            <Route
              path="/error-demo"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading...</p>
                      </div>
                    </div>
                  }
                >
                  <ErrorHandlingDemo />
                </Suspense>
              }
            />
            <Route
              path="/error-showcase"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading...</p>
                      </div>
                    </div>
                  }
                >
                  <ErrorHandlingShowcase />
                </Suspense>
              }
            />
            <Route
              path="/mobile-test"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Loading...</p>
                      </div>
                    </div>
                  }
                >
                  <MobileLayoutTest />
                </Suspense>
              }
            />
            <Route
              path="/admin/*"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">
                          Loading Admin Portal...
                        </p>
                      </div>
                    </div>
                  }
                >
                  <AdminPortal />
                </Suspense>
              }
            />
            <Route path="/" element={renderCurrentPage()} />
            <Route path="*" element={renderCurrentPage()} />
          </Routes>
        </main>

        {showFooter && <Footer />}

        <ToastContainer
          toasts={toasts}
          onDismiss={removeToast}
          position="top-right"
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;
